/**
 * Engine texts in the UI language. Schema 1.1.0 carries English next to every French engine text
 * (message_en, note_en, what_en, step_en) and a stable Fix.kind_code. An engine that still speaks
 * 1.0.0 has none of them: we fall back to the French text and say so (`lang: 'fr'`), so the UI can
 * mark it up with lang="fr" instead of pretending it is English.
 */
import type { Lang } from '../i18n';
import { ROW_LABEL_RE } from '../lib/patternSyntax';
import type { CompileResult, Diagnostic, Fix, StitchMapSize, TraceStep } from './types';

export interface Localized {
  text: string;
  /** Language the text is actually written in. */
  lang: Lang;
}

function pick(lang: Lang, fr: string | null | undefined, en: string | null | undefined): Localized {
  if (lang === 'en' && en) return { text: en, lang: 'en' };
  if (fr) return { text: fr, lang: 'fr' };
  return { text: en ?? '', lang: 'en' };
}

/** The engine's message for a diagnostic (fr) or its English twin (1.1.0). */
export function diagnosticMessage(d: Diagnostic, lang: Lang): Localized {
  // Partial type on purpose: results from a 1.0.0 engine have no message_en.
  return pick(lang, d.message, (d as Partial<Diagnostic>).message_en);
}

export function fixNote(f: Fix, lang: Lang): Localized {
  return pick(lang, f.note, (f as Partial<Fix>).note_en);
}

export function traceWhat(s: TraceStep, lang: Lang): Localized {
  return pick(lang, s.what, (s as Partial<TraceStep>).what_en);
}

export function traceStep(s: TraceStep, lang: Lang): Localized {
  return pick(lang, s.step, (s as Partial<TraceStep>).step_en);
}

export type FixKindCode = Fix['kind_code'];

/** 1.0.0 engines only give the French label: map the known ones to the stable code. */
const KIND_FROM_FR: Record<string, FixKindCode> = {
  nombre: 'number',
  suppression: 'deletion',
  structure: 'structure',
  'fin de rang': 'row_end',
  'compte annoncé': 'stated_count',
  'rangs raccourcis': 'short_rows',
};

export function fixKindCode(f: Fix): FixKindCode {
  const code = (f as Partial<Fix>).kind_code;
  if (code) return code;
  return KIND_FROM_FR[f.kind.trim().toLowerCase()] ?? 'other';
}

export type MapUnavailableReason = NonNullable<StitchMapSize['unavailable_reason']>;

/** Why a size has no stitch map, in the engine's words (1.1.0) or inferred from the result (1.0.0). */
export function mapUnavailableReason(entry: StitchMapSize | null, result: CompileResult): MapUnavailableReason {
  const given = entry?.unavailable_reason;
  if (given) return given;
  const executable = result.rows.some((r) => r.understood && r.per_size.some((p) => p.count_after !== null && p.count_after !== undefined));
  if ((result.module_trace?.length ?? 0) > 0 && !executable) return 'module_mode';
  if (result.rows.length === 0) return 'no_rows';
  if (result.coverage.n_understood === 0 || !executable) return 'not_understood';
  return 'too_large';
}

/**
 * Labels the engine makes up itself (module steps, count checks, charts) have no English field in
 * the schema: a small FR→EN table. Row labels quoted from the pattern are never translated.
 */
const ENGINE_LABELS_EN: Array<[RegExp, string]> = [
  [/^talon$/i, 'heel'],
  [/^gousset$/i, 'gusset'],
  [/^mesures$/i, 'measurements'],
  [/^longueur$/i, 'length'],
  [/^montage$/i, 'cast-on'],
  [/^patron$/i, 'pattern'],
  [/^taille unique$/i, 'one size'],
  [/^grille rang (\d+)$/i, 'chart row $1'],
  [/\(contrôle\)/i, '(check)'],
];

export function localizeLabel(label: string, lang: Lang): string {
  if (lang !== 'en') return label;
  let out = label;
  for (const [re, en] of ENGINE_LABELS_EN) out = out.replace(re, en);
  return out;
}

/** The row label exactly as the pattern writes it at the start of the line (« Tour 5 », "Row 7"). */
function ownLabel(text: string): string | null {
  const m = ROW_LABEL_RE.exec(text);
  return m ? m[1].replace(/\s+/g, ' ').trim() : null;
}

export interface DisplayLabel {
  text: string;
  /** true when the text is a quote of the pattern (never translated, marked up as pattern text). */
  quoted: boolean;
}

/**
 * Label to show for a line: the pattern's own row label when the line starts with one, else the
 * engine label when the line quotes it, else the engine's label in the UI language.
 */
export function displayLabel(lineNo: number, engineLabel: string | null | undefined, result: CompileResult, lang: Lang): DisplayLabel | null {
  if (lineNo < 1) return null;
  const text = result.lines.find((l) => l.line_no === lineNo)?.text ?? '';
  const own = ownLabel(text);
  if (own) return { text: own, quoted: true };
  if (engineLabel) {
    const at = text.toLowerCase().indexOf(engineLabel.toLowerCase());
    if (at >= 0) return { text: text.slice(at, at + engineLabel.length), quoted: true };
    return { text: localizeLabel(engineLabel, lang), quoted: false };
  }
  return null;
}

/** Row label for a finding, only when it is a quote of the pattern (else the line number says where). */
export function findingLabel(lineNo: number, diagLabel: string, result: CompileResult): string | null {
  const row = result.rows.find((r) => r.line_no === lineNo);
  for (const candidate of [diagLabel, row?.label]) {
    const d = displayLabel(lineNo, candidate, result, 'fr');
    if (d?.quoted) return d.text;
  }
  return null;
}
